//! Единая логика обновления access-токена. Используется:
//!  1. Reactive путь: `baseQuery` при ответе 401 — fallback, если по
//!     какой-то причине proactive не успел (часы клиента сильно отстают,
//!     был sleep / hibernate, и т.п.).
//!  2. Proactive путь: `useProactiveTokenRefresh` хук тикает таймер на
//!     `exp - 60 сек` и зовёт `refreshAccessToken` ещё ДО того как сервер
//!     успеет отказать. Юзер в обычной работе никогда не видит 401.
//!
//! Гарантии:
//!  - Мьютекс через `refreshPromise`: одновременно не больше одного запроса
//!    на `/auth/refresh`. Все вызовы (reactive из baseQuery, proactive из
//!    хука, ручной — кнопкой) дожидаются того же Promise.
//!  - Валидация ответа: должны прийти оба токена — иначе считаем провалом
//!    и не пишем мусор в Redux.
//!
//! Не сбрасываем токены и не редиректим здесь — это делает `bailToLogin`
//! в baseQuery только когда refresh реально невозможен.
//!
//! Несколько окон ОСНОВЫ (вкладки, окно исходного листа РД): refresh-токен
//! одноразовый — бэкенд отзывает его при обновлении, а таймеры всех окон с
//! одной парой срабатывают одновременно. Чтобы окно со старым токеном не
//! получило 401 и не вылетело на вход (стерев заодно пару в localStorage),
//! обновление идёт под замком браузера на все окна (`navigator.locks`), и окно
//! сначала смотрит, не записало ли другое окно пару новее, — тогда берёт её.

import type { AppDispatch, RootState } from '@app/store';
import { readPersisted, setTokens } from './slice';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? '/api/v1';
/** Замок обновления на все окна ОСНОВЫ этого браузера. */
const REFRESH_LOCK = 'osnova.auth.refresh';

let refreshPromise: Promise<boolean> | null = null;

/**
 * Пара токенов, которую уже записало другое окно ОСНОВЫ: в localStorage
 * refresh-токен не наш (наш другое окно уже обменяло) — берём её пару.
 *
 * @returns true — пара другого окна принята.
 */
export function adoptStoredTokens(dispatch: AppDispatch, getState: () => RootState): boolean {
  const stored = readPersisted();
  if (!stored.accessToken || !stored.refreshToken) return false;
  if (stored.refreshToken === getState().auth.refreshToken) return false;
  dispatch(setTokens({ accessToken: stored.accessToken, refreshToken: stored.refreshToken }));
  return true;
}

/** `fn` под замком на все окна (Web Locks); браузер без замков — просто `fn`. */
function withRefreshLock<T>(fn: () => Promise<T>): Promise<T> {
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  return locks?.request ? (locks.request(REFRESH_LOCK, fn) as Promise<T>) : fn();
}

/**
 * Запросить новые access+refresh у бэкенда. Идемпотентно: повторный вызов
 * пока первый ещё не завершился — возвращает тот же Promise.
 *
 * @returns true — токены успешно обновлены и записаны в Redux;
 *          false — refresh-токен невалиден/истёк/ответ некорректный.
 */
export function refreshAccessToken(
  dispatch: AppDispatch,
  getState: () => RootState,
): Promise<boolean> {
  if (refreshPromise) return refreshPromise;
  // Пара, которую просили обновить: пока ждали замка, её могло обновить другое
  // окно (и это окно уже подхватило новую по событию storage).
  const requested = getState().auth.refreshToken;
  refreshPromise = (async () => {
    try {
      return await withRefreshLock(async () => {
        // Другое окно уже обновило пару — запрос со старым токеном дал бы 401.
        if (adoptStoredTokens(dispatch, getState)) return true;
        const refreshToken = getState().auth.refreshToken;
        if (!refreshToken) return false;
        if (refreshToken !== requested) return true;
        const res = await fetch(`${API_BASE}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
        // Отказ: возможно, пару только что обновило окно без замка — её и берём.
        if (!res.ok) return adoptStoredTokens(dispatch, getState);
        const data = (await res.json()) as {
          access_token?: string;
          refresh_token?: string;
        };
        if (!data.access_token || !data.refresh_token) return false;
        dispatch(
          setTokens({
            accessToken: data.access_token,
            refreshToken: data.refresh_token,
          }),
        );
        return true;
      });
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();
  return refreshPromise;
}

/**
 * Декодирует exp из JWT (base64url payload). Не валидирует подпись — нам
 * нужно только узнать когда токен истечёт, чтобы вовремя его обновить.
 *
 * @returns Unix-время истечения в миллисекундах, или null если токен
 *          невалидный JSON / нет exp.
 */
export function getTokenExpiryMs(token: string | null): number | null {
  if (!token) return null;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return null;
    // base64url → base64
    const b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    // padding
    const padded = b64.padEnd(b64.length + ((4 - (b64.length % 4)) % 4), '=');
    const json = atob(padded);
    const claims = JSON.parse(json) as { exp?: number };
    if (typeof claims.exp !== 'number') return null;
    return claims.exp * 1000;
  } catch {
    return null;
  }
}

/** Сколько мс осталось до истечения access-токена. null если токена нет. */
export function getTokenTtlMs(token: string | null): number | null {
  const exp = getTokenExpiryMs(token);
  if (exp === null) return null;
  return exp - Date.now();
}
